import {
  Action,
  History,
  Location,
  LocationDescriptorObject,
  LocationListener,
  LocationState,
  Path,
  TransitionPromptHook,
  UnregisterCallback,
  createBrowserHistory,
  parsePath,
} from 'history';

import { CodecOptions, decodeRoute, encodeRoute } from './queryRouteCodec';

/**
 * Thin, version-specific adapter for the `query` router mode.
 *
 * It wraps a real `createBrowserHistory` — so all the hard parts (popstate,
 * history keys/state, go/goBack/goForward, block, listen) come from the `history`
 * package and don't have to be reimplemented — and only transforms the location
 * on the way in and out through {@link queryRouteCodec}:
 *
 *  - the window pathname stays fixed (the client's embed mount path, e.g.
 *    `/experiences/`); the SPA route lives entirely in the query string;
 *  - react-router sees the decoded internal location (`/e/slug`, `/checkout`, …);
 *  - writes encode that internal location back into the query string, preserving
 *    the client's own params.
 *
 * All routing logic lives in the version-agnostic codec, so a future react-router
 * v6/v7 move only reworks this adapter (e.g. feeding the codec to `HistoryRouter`).
 */
export const createQueryHistory = ({
  listIsHome = false,
}: CodecOptions = {}): History => {
  const browserHistory = createBrowserHistory();
  const codecOptions: CodecOptions = { listIsHome };

  const toInternalLocation = (location: Location): Location => {
    const decoded = decodeRoute(location.search);
    return {
      ...location,
      hash: '',
      pathname: decoded.pathname,
      search: decoded.search,
    };
  };

  const toWindowLocation = (
    to: Path | LocationDescriptorObject,
  ): LocationDescriptorObject => {
    const target = typeof to === 'string' ? parsePath(to) : to;
    // A partial location (search-only, no pathname) keeps the CURRENT route, just
    // as react-router's own history resolves a partial descriptor against the
    // current location. Filter/param updates (setParam/replaceParams) navigate
    // this way; without preserving the route they would drop it to the home URL.
    const currentPathname = decodeRoute(
      browserHistory.location.search,
    ).pathname;
    const pathname = target.pathname || currentPathname;
    const search = encodeRoute(
      { pathname, search: target.search ?? '' },
      browserHistory.location.search,
      codecOptions,
    );
    // Keep the real window path; only the query carries the route.
    return { pathname: browserHistory.location.pathname, search, hash: '' };
  };

  const resolveState = (
    to: Path | LocationDescriptorObject,
    state?: LocationState,
  ): LocationState =>
    typeof to === 'object' && to.state !== undefined ? to.state : state;

  const push = (to: Path | LocationDescriptorObject, state?: LocationState) =>
    browserHistory.push(toWindowLocation(to), resolveState(to, state));

  const replace = (
    to: Path | LocationDescriptorObject,
    state?: LocationState,
  ) => browserHistory.replace(toWindowLocation(to), resolveState(to, state));

  const createHref = (location: LocationDescriptorObject): string =>
    browserHistory.createHref(toWindowLocation(location));

  const listen = (listener: LocationListener): UnregisterCallback =>
    browserHistory.listen((location, action) =>
      listener(toInternalLocation(location), action),
    );

  const block = (
    prompt?: boolean | string | TransitionPromptHook,
  ): UnregisterCallback => browserHistory.block(prompt);

  return {
    get length(): number {
      return browserHistory.length;
    },
    get action(): Action {
      return browserHistory.action;
    },
    get location(): Location {
      return toInternalLocation(browserHistory.location);
    },
    push,
    replace,
    go: (n: number) => browserHistory.go(n),
    goBack: () => browserHistory.goBack(),
    goForward: () => browserHistory.goForward(),
    block,
    listen,
    createHref,
  };
};
